import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { API_URL_COMPANIES, API_URL_COMPANIES_V2, API_URL_PREFIX } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { Role, Team, RoleName, UserV2 } from './shared/models';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  roleMap: Map<number, Role> = new Map();
  readonly MAX_ROLES_TO_ATTACH = 5;

  constructor(
    private alcmeonContext: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private httpClient: HttpClient,
    @Inject('$state') private $state: any
  ) {}

  getRoles(): Observable<Role[]> {
    return this.httpClient.get<Role[]>(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/roles`).pipe(
      this.errorHandlingService.catchAlertAndThrowHttpError(),
      map(roles => {
        (roles || []).forEach((role: Role) => {
          this.roleMap.set(role.id, role);
        });
        return roles;
      })
    );
  }

  getTeams(routingTagExists): Observable<Team[]> {
    // In case of a company with no routing tag just return an empty teams array
    if (!routingTagExists) {
      return of([]);
    }
    return this.httpClient
      .get<Team[]>(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/teams`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Allows to retrieve the users without the permissions.
   * @remarks the BE is responsible of the translations of each team and role and returns and array of `{id, label}`.
   * @returns the users
   */
  getUsersV2(): Observable<UserV2[]> {
    return this.httpClient
      .get<UserV2[]>(`${API_URL_COMPANIES_V2}/${this.alcmeonContext.companyId}/users`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createUser(user) {
    return this.httpClient
      .post(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/users`, user)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createRole(role) {
    return this.httpClient
      .post(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/roles`, role)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createTeam(team) {
    return this.httpClient
      .post(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/teams`, team)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  redirectToUserEdit(user, newTab = false) {
    if (newTab) {
      const target = this.$state.href('auth.alcmeon.fuse.user-edit', {
        companyId: this.alcmeonContext.companyId,
        id: user.id,
      });
      window.open(`${API_URL_PREFIX}${target}`, '_blank');
    } else {
      this.$state.go('auth.alcmeon.fuse.user-edit', {
        companyId: this.alcmeonContext.companyId,
        id: user.id,
      });
    }
  }

  redirectToRoleEdit(role, trackTable) {
    this.$state.go('auth.alcmeon.fuse.role-edit', {
      companyId: this.alcmeonContext.companyId,
      role,
      trackTable,
    });
  }

  redirectToTeamEdit(team, trackTable) {
    this.$state.go('auth.alcmeon.fuse.team-edit', {
      companyId: this.alcmeonContext.companyId,
      team,
      trackTable,
    });
  }

  updateTeamsOrder(companyId, teams) {
    return this.httpClient.put(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/teams`, {
      display_order: teams.map(t => t.tag_id),
    });
  }

  sortUsersSitesRoles(items, sortKey) {
    items.sort((x, y) => {
      if (x[sortKey] < y[sortKey]) {
        return -1;
      }
      if (x[sortKey] > y[sortKey]) {
        return 1;
      }
      return 0;
    });
  }

  deleteRole(id: number) {
    return this.httpClient
      .delete(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/roles/${id}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  replaceTeam(oldTeamTagId, newTeamTagId) {
    return this.httpClient
      .delete(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/teams/${oldTeamTagId}/${newTeamTagId}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getRoleNames(): Observable<RoleName[]> {
    return this.httpClient
      .get<RoleName[]>(`${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/role-names`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
